// src/App.js
import React, { useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { initPerformanceMonitoring } from './utils/performanceTest';
import useViewportHeight from './hooks/useViewportHeight';

import './styles/viewport.css'; // Dynamic viewport height support (must be first)
import './styles/layouts.css';
import './styles/ContentPages.css';
import './styles/Header.css';
import './styles/mobile.css';
// import './styles/App.css';

// Провайдеры
import { AuthProvider } from './components/AuthContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ChatProvider } from './contexts/ChatContext';

// Лейауты (eager — маленькие, нужны на каждом роуте)
import DesktopAppLayout from './components/layouts/DesktopAppLayout';
import MobileAppLayout from './components/layouts/MobileAppLayout';
import DesktopPanelLayout from './components/layouts/DesktopPanelLayout';
import PageLoader from './components/PageLoader';
import ChunkErrorBoundary from './components/ChunkErrorBoundary';
import { useIsMobile } from './hooks/useResponsive';
import { Provider } from 'react-redux';
import store from './store';

// Страницы (lazy — загружаются только при переходе на роут)
const Index = React.lazy(() => import('./pages/IndexPage'));
const IndexPageMobile = React.lazy(() => import('./pages/mobile/IndexPageMobile'));
const ProfilePageMobile = React.lazy(() => import('./pages/mobile/ProfilePageMobile'));
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const RegisterPage = React.lazy(() => import('./pages/RegisterPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const PostDetailsPage = React.lazy(() => import('./pages/posts/PostDetailsPage'));
const PostEditPage = React.lazy(() => import('./pages/posts/PostEditPage'));
const PostCreatePage = React.lazy(() => import('./pages/posts/PostCreatePage'));
const RepeatTripPage = React.lazy(() => import('./pages/posts/RepeatTripPage'));
const AuthorPage = React.lazy(() => import('./pages/posts/AuthorPage'));
const CompanionsPage = React.lazy(() => import('./pages/companions/desktop/CompanionsPage'));
const CompanionsPageMobile = React.lazy(() => import('./pages/companions/mobile/CompanionsPageMobile'));
const TripDetailPage = React.lazy(() => import('./pages/companions/desktop/TripDetailPage'));
const TripCreatePage = React.lazy(() => import('./pages/companions/desktop/TripCreatePage'));
const TripCandidatesPage = React.lazy(() => import('./pages/companions/desktop/TripCandidatesPage'));
const ForgotPasswordPage = React.lazy(() => import('./pages/ForgotPasswordPage'));
const PasswordResetConfirmPage = React.lazy(() => import('./pages/PasswordResetConfirmPage'));
const UserAgreementPage = React.lazy(() => import('./pages/UserAgreementPage'));
const PrivacyPolicyPage = React.lazy(() => import('./pages/PrivacyPolicyPage'));
const AboutProjectPage = React.lazy(() => import('./pages/AboutProjectPage'));
const FeedbackPage = React.lazy(() => import('./pages/FeedbackPage'));
const ContactsPage = React.lazy(() => import('./pages/ContactsPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));
const ChatPage = React.lazy(() => import('./pages/ChatPage'));
const ChatPageMobile = React.lazy(() => import('./pages/mobile/ChatPageMobile'));

const App = () => {
  const isMobile = useIsMobile();
  
  // Initialize dynamic viewport height tracking (handles mobile browser bar changes)
  useViewportHeight();
  
  // Заголовок вкладки и инициализация мониторинга производительности
  useEffect(() => {
    document.title = 'Хочувлето!';
    
    // Initialize performance monitoring for image loading
    initPerformanceMonitoring();
  }, []);

  return (
    <Provider store={store}>
    <AuthProvider>
      <NotificationProvider>
        <ChatProvider>
          <CurrencyProvider>
            <Router>
          <ChunkErrorBoundary>
            <Suspense fallback={<PageLoader fullPage />}>
            <Routes>
          {/* ===== Лейаут БЕЗ Header (панель по центру) ===== */}
          <Route
            path="/about"
            element={
              <DesktopPanelLayout>
                <AboutProjectPage />
              </DesktopPanelLayout>
            }
          />
          <Route
            path="/privacy-policy"
            element={
              <DesktopPanelLayout>
                <PrivacyPolicyPage />
              </DesktopPanelLayout>
            }
          />
          <Route
            path="/user-agreement"
            element={
              <DesktopPanelLayout>
                <UserAgreementPage />
              </DesktopPanelLayout>
            }
          />
          <Route
            path="/contacts"
            element={
              <DesktopPanelLayout>
                <ContactsPage />
              </DesktopPanelLayout>
            }
          />
          <Route
            path="/feedback"
            element={
              <DesktopPanelLayout>
                <FeedbackPage />
              </DesktopPanelLayout>
            }
          />
          <Route
            path="/login"
            element={
                <DesktopPanelLayout>
                  <LoginPage />
                </DesktopPanelLayout>
            }
          />
          <Route
            path="/register"
            element={
              <DesktopPanelLayout>
                <RegisterPage />
              </DesktopPanelLayout>
            }
          />
          <Route
            path="/password-reset"
            element={
              <DesktopPanelLayout>
                <ForgotPasswordPage />
              </DesktopPanelLayout>
            }
          />
          <Route
            path="/password-reset/confirm/:uid/:token"
            element={
              <DesktopPanelLayout>
                <PasswordResetConfirmPage />
              </DesktopPanelLayout>
            }
          />

          {/* ===== Лейаут С Header (основные страницы) ===== */}
          <Route
            path="/"
            element={
              isMobile ? (
                <MobileAppLayout>
                  <IndexPageMobile />
                </MobileAppLayout>
              ) : (
                <DesktopAppLayout>
                  <Index />
                </DesktopAppLayout>
              )
            }
          />
          <Route
            path="/profile"
            element={
              isMobile ? (
                <MobileAppLayout>
                  <ProfilePageMobile />
                </MobileAppLayout>
              ) : (
                <DesktopAppLayout>
                  <ProfilePage />
                </DesktopAppLayout>
              )
            }
          />

          <Route
            path="/posts/:postId"
            element={
              isMobile ? (
                <MobileAppLayout>
                  <PostDetailsPage />
                </MobileAppLayout>
              ) : (
                <DesktopAppLayout>
                  <PostDetailsPage />
                </DesktopAppLayout>
              )
            }
          />
          <Route
            path="/posts/:postId/edit"
            element={
              isMobile ? (
                <MobileAppLayout>
                  <PostEditPage />
                </MobileAppLayout>
              ) : (
                <DesktopAppLayout>
                  <PostEditPage />
                </DesktopAppLayout>
              )
            }
          />
          <Route
            path="/posts/:postId/repeat-trip"
            element={
              isMobile ? (
                <MobileAppLayout>
                  <RepeatTripPage />
                </MobileAppLayout>
              ) : (
                <DesktopAppLayout>
                  <RepeatTripPage />
                </DesktopAppLayout>
              )
            }
          />
          <Route
            path="/post-create"
            element={
              isMobile ? (
                <MobileAppLayout>
                  <PostCreatePage />
                </MobileAppLayout>
              ) : (
                <DesktopAppLayout>
                  <PostCreatePage />
                </DesktopAppLayout>
              )
            }
          />
          <Route
            path="/author/:authorId"
            element={
              isMobile ? (
                <MobileAppLayout>
                  <AuthorPage />
                </MobileAppLayout>
              ) : (
                <DesktopAppLayout>
                  <AuthorPage />
                </DesktopAppLayout>
              )
            }
          />
          <Route
            path="/chat"
            element={
              isMobile ? (
                <MobileAppLayout>
                  <ChatPageMobile />
                </MobileAppLayout>
              ) : (
                <DesktopAppLayout>
                  <ChatPage />
                </DesktopAppLayout>
              )
            }
          />

          {/* ===== Companions (Попутчик) routes ===== */}
          <Route
            path="/companions"
            element={
              isMobile ? (
                <MobileAppLayout>
                  <CompanionsPageMobile />
                </MobileAppLayout>
              ) : (
                <DesktopAppLayout>
                  <CompanionsPage />
                </DesktopAppLayout>
              )
            }
          />
          <Route
            path="/companions/create"
            element={
              isMobile ? (
                <MobileAppLayout>
                  <TripCreatePage />
                </MobileAppLayout>
              ) : (
                <DesktopAppLayout>
                  <TripCreatePage />
                </DesktopAppLayout>
              )
            }
          />
          <Route
            path="/companions/:tripId"
            element={
              isMobile ? (
                <MobileAppLayout>
                  <TripDetailPage />
                </MobileAppLayout>
              ) : (
                <DesktopAppLayout>
                  <TripDetailPage />
                </DesktopAppLayout>
              )
            }
          />
          <Route
            path="/companions/:tripId/candidates"
            element={
              isMobile ? (
                <MobileAppLayout>
                  <TripCandidatesPage />
                </MobileAppLayout>
              ) : (
                <DesktopAppLayout>
                  <TripCandidatesPage />
                </DesktopAppLayout>
              )
            }
          />
          {/* ===== 404 — catch-all (must be last) ===== */}
          <Route
            path="*"
            element={
              <DesktopPanelLayout>
                <NotFoundPage />
              </DesktopPanelLayout>
            }
          />
          </Routes>
            </Suspense>
          </ChunkErrorBoundary>
          </Router>
        </CurrencyProvider>
        </ChatProvider>
      </NotificationProvider>
    </AuthProvider>
    </Provider>
  );
};

export default App;
